import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from "@common/axios";
import {Alert } from 'antd';
import {BrandContext} from "@context/BrandContext";
import CircularLoader from "@common/CircularLoader";
import {ContentContext} from "@context/ContentContext";

const VideoPlayer = props => {
    const {mediaId, conceptId, displayInDialog = false, displayHeader = true} = props;
    const brandConfig = useContext(BrandContext);
    const {content, dialogMediaObject, setDialogMediaObject} = useContext(ContentContext);
    const mediaConceptId = conceptId ? conceptId : content.currentConcept.conceptId;
    const [media, setMedia] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const videoNode = useRef(null);
    const player = useRef(null);

    useEffect(() => {
        if (mediaId == null || mediaConceptId == null) return;

        setLoading(true);
        setError(null);

        axios.get('/web/ui/media/media', {params: {mediaId: mediaId, conceptId: mediaConceptId}})
            .then(res => {
                if(displayInDialog) {
                    setDialogMediaObject({...dialogMediaObject, mediaName: res.data.mediaName});
                }

                setMedia(res.data)
            })
            .catch(err => {
                console.error('Video load failed:', err);
                setError(err);
            })
            .finally(() => setLoading(false));
    }, [mediaId, mediaConceptId]);

    // Initialize Video.js when media is available
    useEffect(() => {
        if (!media || !videoNode.current) return;

        // player options
        const options = {
            controls: true,
            autoplay: true,
            preload: 'auto',
            fluid: true,
            poster: media.imageUrl,
            sources: [{
                src: media.url,
                type: media.contentType
            }]
        };

        if (media.subtitleUrl) {
            options.tracks = [{
                kind: 'captions',
                src: media.subtitleUrl,
                srclang: 'en',
                label: 'English',
                default: true
            }];
        }

        player.current = videojs(videoNode.current, options);

        return () => {
            if (player.current) {
                player.current.dispose();
                player.current = null;
            }
        };
    }, [media]);

    if (loading) {
        return <CircularLoader />;
    }

    if (error) {
        return <Alert
            type="error"
            showIcon
            message="Failed to load video"
            description={error.message}
        />;
    }

    if (!media) {
        const videoLabel = brandConfig.videoName.toLowerCase();
        const conceptLabel = brandConfig.conceptName.toLowerCase();
        return (
            <div>
                <Alert
                    type="error"
                    showIcon
                    message={`There are no ${videoLabel}s in this ${conceptLabel}.`}
                />
            </div>
        );
    }

    return (
        <>
            {loading && <CircularLoader />}
            <div className="ch-video-player">
                {displayHeader && !dialogMediaObject && (
                    <h3>{media.mediaName}</h3>
                )}
                <div data-vjs-player>
                    <video ref={videoNode}
                        id={`video-${media.mediaId}`}
                        className="video-js vjs-default-skin vjs-16-9"
                    />
                </div>
            </div>
        </>
    );
};

VideoPlayer.propTypes = {
    mediaId:   PropTypes.number.isRequired
};

export default VideoPlayer;
